import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, XCircle, FileText, Loader2, Users, Shield, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommitteeTenderView() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // 'approve' | 'reject'
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/committee/tenders/${tenderId}`, { withCredentials: true });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tenderId]);

  const requestOtp = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/committee/otp/request`, { tender_id: tenderId, action }, { withCredentials: true });
      toast.success('OTP sent to your email');
      setOtpSent(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const submitDecision = async () => {
    if (!otp || otp.length !== 6) return toast.error('Enter the 6-digit OTP');
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/committee/otp/verify-and-decide`, { tender_id: tenderId, otp, decision: action, remarks }, { withCredentials: true });
      toast.success(`Decision recorded — submission status: ${r.submission_status.toUpperCase()}`);
      setAction(null); setOtpSent(false); setOtp(''); setRemarks('');
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin h-8 w-8 text-navy" /></div>;
  const t = data?.tender || {};
  const cw = t.committee_workflow || {};
  const sub = (cw.submissions || []).find(s => s.id === cw.active_submission_id);
  const my = data?.my_decision;
  const approvals = data?.approvals || [];
  const bids = data?.bids || [];
  const recommendedBid = bids.find(b => b.id === sub?.recommended_bid_id);

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="committee-tender-view">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate('/committee/dashboard')} className="text-sm text-navy font-semibold mb-4 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back to Committee Dashboard</button>
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-4">
          <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-gold" /><span className="text-xs font-bold uppercase text-navy">Committee Approval Package</span></div>
          <h1 className="text-2xl font-black text-navy">{t.title}</h1>
          <div className="text-sm text-slate-600 mt-1">{t.work_category} · {t.location} · Estimated Budget: {t.estimated_value ? `₹${Number(t.estimated_value).toLocaleString('en-IN')}` : 'Not Available'}</div>
        </div>

        <Card title="Manager Recommendation">
          {sub ? (
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <KV k="Recommended Supplier" v={sub.recommended_supplier_name || '—'} />
              <KV k="Recommended Amount" v={sub.recommended_amount ? `₹${Number(sub.recommended_amount).toLocaleString('en-IN')}` : '—'} />
              <KV k="Approval Policy" v={`Minimum ${sub.min_approvals} of ${sub.total_members} members`} />
              <KV k="Resolution" v={`v${sub.resolution_version} · ${sub.resolution_number}`} />
              {sub.manager_notes && <div className="sm:col-span-2"><div className="text-xs uppercase text-slate-500 font-bold">Manager Notes</div><div className="text-slate-700 mt-1">{sub.manager_notes}</div></div>}
            </div>
          ) : <div className="text-sm text-slate-500">No active submission.</div>}
        </Card>

        <Card title="Comparative Statement (Bids)">
          {bids.length === 0 ? <div className="text-sm text-slate-500">No bids on record.</div> : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b"><tr><th className="text-left py-2">Supplier</th><th className="text-right">Quoted Amount</th><th className="text-center">Status</th></tr></thead>
              <tbody>
                {bids.map(b => (
                  <tr key={b.id} className={`border-b ${b.id === sub?.recommended_bid_id ? 'bg-amber-50 font-bold' : ''}`}>
                    <td className="py-2">{b.supplier?.company_name || b.supplier_name || b.supplier_id}</td>
                    <td className="text-right">₹{Number(b.quoted_amount || 0).toLocaleString('en-IN')}</td>
                    <td className="text-center text-xs">{b.id === sub?.recommended_bid_id ? <span className="text-navy font-bold">RECOMMENDED</span> : (b.status || '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title={`Committee Approvals (${approvals.length} / ${sub?.total_members || 0})`}>
          {approvals.length === 0 ? <div className="text-sm text-slate-500">No decisions recorded yet.</div> : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b"><tr><th className="text-left py-2">#</th><th className="text-left">Member</th><th className="text-left">Decision</th><th className="text-left">Timestamp</th><th className="text-left">Remarks</th></tr></thead>
              <tbody>
                {approvals.map(a => (
                  <tr key={a.id} className="border-b">
                    <td className="py-2">{a.display_order || '—'}</td>
                    <td>{a.member_name} <span className="text-xs text-slate-500">({a.designation})</span></td>
                    <td>{a.decision === 'approve' ? <span className="text-green-700 font-bold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Approved</span> : <span className="text-red-700 font-bold flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejected</span>}</td>
                    <td className="text-xs text-slate-600">{new Date(a.timestamp).toLocaleString('en-IN')}</td>
                    <td className="text-xs text-slate-700">{a.remarks || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Decision */}
        {!my && sub?.status !== 'approved' && sub?.status !== 'failed' && (
          <Card title="Your Decision" testid="decision-card">
            {!action ? (
              <div className="flex gap-3">
                <button data-testid="btn-approve" onClick={() => setAction('approve')} className="px-5 py-3 rounded-lg bg-green-600 text-white font-bold flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Approve</button>
                <button data-testid="btn-reject" onClick={() => setAction('reject')} className="px-5 py-3 rounded-lg bg-red-600 text-white font-bold flex items-center gap-2"><XCircle className="w-4 h-4" /> Reject</button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm">You chose to <strong className={action === 'approve' ? 'text-green-700' : 'text-red-700'}>{action.toUpperCase()}</strong> this procurement.</div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Remarks (optional)</label>
                  <textarea value={remarks} onChange={e => setRemarks(e.target.value)} className="mt-1 w-full px-3 py-2 rounded border border-slate-200 text-sm" rows={2} data-testid="remarks-input" />
                </div>
                {!otpSent ? (
                  <div className="flex gap-2">
                    <button onClick={requestOtp} disabled={busy} className="px-4 py-2 rounded bg-navy text-white text-sm font-bold flex items-center gap-1 disabled:opacity-50" data-testid="btn-request-otp"><Send className="w-3 h-3" /> {busy ? 'Sending…' : 'Send OTP to my email'}</button>
                    <button onClick={() => { setAction(null); setRemarks(''); }} className="px-4 py-2 rounded border border-slate-200 text-sm">Cancel</button>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Enter 6-digit OTP</label>
                    <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} className="mt-1 w-40 px-3 py-2 rounded border border-slate-200 text-lg tracking-widest text-center" maxLength={6} data-testid="otp-input" />
                    <div className="flex gap-2 mt-3">
                      <button onClick={submitDecision} disabled={busy} className="px-4 py-2 rounded bg-navy text-white text-sm font-bold disabled:opacity-50" data-testid="btn-confirm-decision">{busy ? 'Verifying…' : `Confirm ${action.toUpperCase()}`}</button>
                      <button onClick={requestOtp} disabled={busy} className="px-4 py-2 rounded border border-slate-200 text-sm">Resend OTP</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}
        {my && (
          <Card title="Your Decision">
            <div className={`p-4 rounded ${my.decision === 'approve' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className={`font-bold ${my.decision === 'approve' ? 'text-green-800' : 'text-red-800'}`}>{my.decision === 'approve' ? 'You approved this procurement.' : 'You rejected this procurement.'}</div>
              <div className="text-xs text-slate-600 mt-1">Recorded {new Date(my.timestamp).toLocaleString('en-IN')} · OTP verified · IP {my.ip || '—'}</div>
              {my.remarks && <div className="text-xs text-slate-700 mt-1">Remarks: {my.remarks}</div>}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-4" data-testid={testid}>
      <h2 className="text-lg font-bold text-navy mb-4">{title}</h2>
      {children}
    </div>
  );
}
function KV({ k, v }) { return <div><div className="text-xs uppercase text-slate-500 font-bold">{k}</div><div className="text-slate-800 mt-0.5">{v}</div></div>; }
